/** Manage → API: Git webhooks (GitHub / GitLab) and Telegram groups; My Account: personal Telegram. */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorBox, Panel, fmtDate } from '../components';
import { CopyLink } from './ManageTeam';
import { t } from '../i18n';

interface GitHook {
  id: number;
  name: string;
  user: string;
  resolve_on_fix: boolean;
  created_at: string;
  last_used_at: string | null;
}

export function GitPanel() {
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ['git-hooks'], queryFn: () => api.get<GitHook[]>('/api/manage/git-hooks') });
  const [name, setName] = useState('');
  const [created, setCreated] = useState<{ name: string; url: string; secret: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['git-hooks'] });
  const create = useMutation({
    mutationFn: () => api.post<{ url: string; secret: string }>('/api/manage/git-hooks', { name: name.trim() }),
    onSuccess: (r) => {
      setCreated({ name: name.trim(), ...r });
      setName('');
      refresh();
    },
  });
  const toggle = useMutation({ mutationFn: (h: GitHook) => api.patch(`/api/manage/git-hooks/${h.id}`, { resolve_on_fix: !h.resolve_on_fix }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: number) => api.del(`/api/manage/git-hooks/${id}`), onSuccess: refresh });

  return (
    <Panel
      title={t('Git (GitHub, GitLab)')}
      actions={
        <form
          className="inline-form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Name, e.g. GitHub app')} maxLength={80} />
          <button className="primary" disabled={!name.trim() || create.isPending}>
            {t('Create webhook')}
          </button>
        </form>
      }
    >
      <p className="muted small">
        {t('Commits that mention #123 add a note to issue 123; "fixes #123" (or closes, resolves) also resolves it. Notes are written in the name of the person who creates the webhook.')}
      </p>
      <ErrorBox error={error || create.error || toggle.error || remove.error} />
      {created && (
        <div className="banner success git-setup">
          <div>{t('Webhook "{name}" created. Copy the address and the secret now — they are shown only once.', { name: created.name })}</div>
          <div>
            {t('Payload URL')}: <CopyLink text={created.url} />
          </div>
          <div>
            {t('Secret')}: <CopyLink text={created.secret} />
          </div>
          <ul className="small">
            <li>{t('GitHub: repository → Settings → Webhooks → Add webhook; content type application/json, the secret as "Secret", event "Just the push event".')}</li>
            <li>{t('GitLab: project → Settings → Webhooks; the address as URL, the secret as "Secret token", trigger "Push events".')}</li>
          </ul>
        </div>
      )}
      {data && data.length > 0 && (
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Name')}</th>
              <th>{t('Notes as')}</th>
              <th>{t('Resolve on "fixes #N"')}</th>
              <th>{t('Created')}</th>
              <th>{t('Last used')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.map((h) => (
              <tr key={h.id}>
                <td>{h.name}</td>
                <td>{h.user}</td>
                <td>
                  <input type="checkbox" checked={h.resolve_on_fix} onChange={() => toggle.mutate(h)} aria-label={t('Resolve on "fixes #N"')} />
                </td>
                <td className="nowrap">{fmtDate(h.created_at, false)}</td>
                <td className="nowrap">{h.last_used_at ? fmtDate(h.last_used_at) : '—'}</td>
                <td>
                  <button className="link danger" onClick={() => confirm(t('Delete the webhook "{name}"?', { name: h.name })) && remove.mutate(h.id)}>
                    {t('Delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function TelegramGroupsPanel() {
  const qc = useQueryClient();
  const { data, error } = useQuery({
    queryKey: ['telegram-groups'],
    queryFn: () => api.get<{ enabled: boolean; bot: string | null; chats: { id: number; title: string; created_at: string }[] }>('/api/manage/telegram'),
  });
  const [command, setCommand] = useState<{ bot: string | null; command: string } | null>(null);
  const link = useMutation({ mutationFn: () => api.post<{ bot: string | null; command: string }>('/api/manage/telegram/link'), onSuccess: setCommand });
  const remove = useMutation({ mutationFn: (id: number) => api.del(`/api/manage/telegram/chats/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-groups'] }) });
  if (!data?.enabled) return null;
  return (
    <Panel
      title={t('Telegram groups')}
      actions={
        <button className="primary" disabled={link.isPending} onClick={() => link.mutate()}>
          {t('Connect a group')}
        </button>
      }
    >
      <p className="muted small">{t('A connected group gets new issues, changes and public notes of this team.')}</p>
      <ErrorBox error={error || link.error || remove.error} />
      {command && (
        <div className="banner success">
          {t('Add {bot} to the group, then send this message in the group (valid for an hour):', { bot: command.bot ? `@${command.bot}` : t('the bot') })}{' '}
          <CopyLink text={command.command} />
        </div>
      )}
      {data.chats.length > 0 && (
        <table className="simple">
          <tbody>
            {data.chats.map((c) => (
              <tr key={c.id}>
                <td>{c.title || t('(group without a name)')}</td>
                <td className="nowrap">{fmtDate(c.created_at, false)}</td>
                <td>
                  <button className="link danger" onClick={() => remove.mutate(c.id)}>
                    {t('Disconnect')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

/** My Account: notifications in a private chat with the bot. */
export function TelegramAccountPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['account-telegram'],
    queryFn: () => api.get<{ enabled: boolean; bot: string | null; connected: boolean }>('/api/account/telegram'),
  });
  const [url, setUrl] = useState<string | null>(null);
  const link = useMutation({
    mutationFn: () => api.post<{ url: string }>('/api/account/telegram/link'),
    onSuccess: (r) => {
      setUrl(r.url);
      window.open(r.url, '_blank', 'noopener');
    },
  });
  const disconnect = useMutation({ mutationFn: () => api.del('/api/account/telegram'), onSuccess: () => qc.invalidateQueries({ queryKey: ['account-telegram'] }) });
  if (!data?.enabled) return null;
  return (
    <Panel title={t('Telegram')}>
      {data.connected ? (
        <p>
          {t('Your notifications also come to Telegram.')}{' '}
          <button className="link danger" onClick={() => disconnect.mutate()}>
            {t('Disconnect')}
          </button>
        </p>
      ) : (
        <>
          <p className="muted">{t('Get the notifications of the bell in a chat with our Telegram bot.')}</p>
          <button className="primary" disabled={link.isPending} onClick={() => link.mutate()}>
            {t('Connect Telegram')}
          </button>
          {url && (
            <p className="small">
              {t('Telegram opens; press "Start" in the chat. If it did not open:')} <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>{' '}
              <button className="link" onClick={() => qc.invalidateQueries({ queryKey: ['account-telegram'] })}>
                {t('Check again')}
              </button>
            </p>
          )}
        </>
      )}
      <ErrorBox error={link.error || disconnect.error} />
    </Panel>
  );
}
